import {JSXMapSerializer, PrismicRichText} from '@prismicio/react'
import {useRef, useEffect, useMemo, useCallback, useLayoutEffect} from 'react'
import {ANIMATION_DEFAULTS, CSS_CLASSES} from './constants'
import {ContentSectionProps, MobileSlots} from './types'
import {calculateScrollMetrics, calculateWordAnimationState, isParagraphComplete} from './animationCalculations'
import {ParagraphComponent} from './textComponents/ParagraphComponent'
import {SpanComponent} from './textComponents/SpanComponent'
import {useParagraphCollection} from 'slices/AnimatedTextBlock/hooks/useParagraphCollection'

// Main component
export default function ScrollSection(props: ContentSectionProps) {
  const {field, className, mobileSlots = {}, animationConfig} = props

  // Extract config with defaults
  const config = {
    fadeRatio: animationConfig?.fadeRatio ?? ANIMATION_DEFAULTS.FADE_RATIO,
    minOpacity: animationConfig?.minOpacity ?? ANIMATION_DEFAULTS.MIN_OPACITY,
  }

  const rootRef = useRef<HTMLDivElement | null>(null)
  const {paragraphsRef, wordsRef, collectParagraphs} = useParagraphCollection()
  const ticking = useRef(false)

  // Core opacity computation
  const updateOpacity = useCallback(() => {
    if (!rootRef.current || !paragraphsRef.current || !paragraphsRef.current.length) {
      return
    }

    const paragraphs = paragraphsRef.current

    let totalWords = 0

    for (let pIndex = 0; pIndex < paragraphs.length; pIndex++) {
      const {words} = paragraphs[pIndex]

      totalWords += words.length
    }

    const firstParagraph = paragraphs[0]?.el
    const lastParagraph = paragraphs[paragraphs.length - 1]?.el

    if (!firstParagraph || !lastParagraph) {
      return
    }

    const firstRect = firstParagraph.getBoundingClientRect()
    const lastRect = lastParagraph.getBoundingClientRect()
    const scrollMetrics = calculateScrollMetrics(config.fadeRatio, firstRect, lastRect)

    const currentWordFloat = scrollMetrics.scrollProgress * totalWords
    const currentWordIndex = Math.floor(currentWordFloat)
    const wordProgress = currentWordFloat - currentWordIndex

    let wordIndex = 0
    let previousParagraphsDone = true

    for (let pIndex = 0; pIndex < paragraphs.length; pIndex++) {
      const {words} = paragraphs[pIndex]

      if (!words.length) {
        continue
      }

      const paragraphEndWordIndex = wordIndex + words.length - 1
      const shouldAnimateParagraph: boolean = previousParagraphsDone

      for (let i = 0; i < words.length; i++) {
        const globalWordIndex = wordIndex + i

        const animationState = calculateWordAnimationState(
          globalWordIndex,
          currentWordIndex,
          wordProgress,
          config.minOpacity,
          shouldAnimateParagraph,
        )

        const opacityString = animationState.opacity.toFixed(3)
        const blurString = `blur(${animationState.blur.toFixed(2)}px)`

        if (words[i].style.opacity !== opacityString) {
          words[i].style.opacity = opacityString
        }

        words[i].style.filter = blurString
      }

      wordIndex += words.length

      const paragraphComplete = isParagraphComplete(currentWordIndex, paragraphEndWordIndex, wordProgress)

      previousParagraphsDone = shouldAnimateParagraph && paragraphComplete
    }
  }, [config.fadeRatio, config.minOpacity, paragraphsRef])

  const components = useMemo<JSXMapSerializer>(
    () => ({
      paragraph: ({children, node}) => (
        <ParagraphComponent node={node} mobileSlots={mobileSlots}>
          {children}
        </ParagraphComponent>
      ),
      strong: ({children}) => <strong className={CSS_CLASSES.STRONG}>{children}</strong>,
      span: ({text}) => <SpanComponent text={text} />,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- No need to re-create components
    [],
  )

  // DOM setup
  useLayoutEffect(() => {
    if (!rootRef.current) {
      return
    }

    let mo: MutationObserver

    const collectAndUpdate = () => {
      // Temporarily disconnect observer to prevent infinite loop
      if (mo) {
        mo.disconnect()
      }

      collectParagraphs(rootRef.current, config.minOpacity)
      updateOpacity()

      // Reconnect observer after DOM changes are complete
      if (rootRef.current && mo) {
        mo.observe(rootRef.current, {childList: true, subtree: true, characterData: true})
      }
    }

    // Initial setup
    collectParagraphs(rootRef.current, config.minOpacity)
    updateOpacity()
    rootRef.current.classList.add('fx-ready')

    // Set up observer for content changes
    mo = new MutationObserver(collectAndUpdate)
    mo.observe(rootRef.current, {childList: true, subtree: true, characterData: true})

    return () => mo.disconnect()
  }, [field, collectParagraphs, updateOpacity, config.minOpacity])

  // Event handlers
  useEffect(() => {
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReduced) {
      for (const el of wordsRef.current || []) {
        el.style.opacity = '1'
        el.style.filter = 'blur(0px)'
      }

      return
    }

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true

        requestAnimationFrame(() => {
          ticking.current = false
          updateOpacity()
        })
      }
    }

    const onResize = () => {
      collectParagraphs(rootRef.current, config.minOpacity)
      updateOpacity()
    }

    window.addEventListener('scroll', onScroll, {passive: true})
    window.addEventListener('resize', onResize)

    const ro = new ResizeObserver(() => {
      collectParagraphs(rootRef.current, config.minOpacity)
      updateOpacity()
    })

    if (rootRef.current) {
      ro.observe(rootRef.current)
    }

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      ro.disconnect()
    }
  }, [config.minOpacity, updateOpacity, collectParagraphs, wordsRef])

  return (
    <div ref={rootRef} className={CSS_CLASSES.ROOT + (className ? ' ' + className : '')}>
      <PrismicRichText field={field} components={components} />
    </div>
  )
}

export type {MobileSlots, ContentSectionProps}
